import {
    useState,
    useRef
} from "react";
import {
    useWindowDimensions,
    TouchableOpacity,
    Platform,
    Image,
    Modal,
    View
} from "react-native";
import type IPostPhotosManagerProps from "./type";
import type {
    PostPhotoItem
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    Button,
    Text
} from "ncore-ui-kit";
import type {
    DriveFilePayloadType
} from "../driveSelectorModal/type";
import {
    ChevronRightIcon,
    ChevronLeftIcon,
    Maximize2Icon,
    Trash2Icon,
    UploadIcon,
    FolderIcon,
    ImageIcon,
    StarIcon,
    XIcon
} from "lucide-react-native";
import DriveSelectorModal from "../driveSelectorModal";
import SmartCoverImage from "../smartCoverImage";

const PostPhotosManager = ({
    disabled = false,
    maxPhotos = 10,
    onChange,
    photos
}: IPostPhotosManagerProps) => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [
        previewModalUrl,
        setPreviewModalUrl
    ] = useState<string | null>(null);

    const [
        draggedIndex,
        setDraggedIndex
    ] = useState<number | null>(null);

    const [
        isDriveModalVisible,
        setIsDriveModalVisible
    ] = useState<boolean>(false);

    const handleFileInputClick = () => {
        if(disabled) {
            return;
        }
        if(fileInputRef.current) {
            fileInputRef.current.value = "";
            fileInputRef.current.click();
        }
    };

    const handleFilesSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const fileList = e.target.files;
        if(!fileList || fileList.length === 0) {
            return;
        }

        const remainingSlots = maxPhotos - photos.length;
        if(remainingSlots <= 0) {
            NCoreUIKitSnackBar.open({
                title: `${localize("post-photos-max-limit-reached")} (${maxPhotos})`
            });
            return;
        }

        const newItems: PostPhotoItem[] = [];
        const maxFileSize = 10 * 1024 * 1024;
        const filesToProcess = Array.from(fileList).slice(0, remainingSlots);

        filesToProcess.forEach(file => {
            if(file.size > maxFileSize) {
                NCoreUIKitSnackBar.open({
                    title: `${file.name} - ${localize("members-invalid-file-size")}`
                });
                return;
            }
            const previewUrl = URL.createObjectURL(file);
            newItems.push({
                order: photos.length + newItems.length,
                source: "upload",
                title: file.name,
                previewUrl,
                file
            });
        });

        if(newItems.length > 0) {
            const updated = [
                ...photos,
                ...newItems
            ].map((p, idx) => ({
                ...p,
                order: idx
            }));
            onChange(updated);
        }
    };

    const handleDriveFileSelect = (url: string, driveFile?: DriveFilePayloadType) => {
        setIsDriveModalVisible(false);
        if(photos.length >= maxPhotos) {
            NCoreUIKitSnackBar.open({
                title: `${localize("post-photos-max-limit-reached")} (${maxPhotos})`
            });
            return;
        }

        let cleanPath = driveFile?.filePath || url;
        try {
            if(cleanPath.startsWith("http")) {
                const parsedUrl = new URL(cleanPath);
                cleanPath = parsedUrl.pathname;
            }
        } catch {
            // ignore
        }
        const queryIndex = cleanPath.indexOf("?");
        if(queryIndex !== -1) {
            cleanPath = cleanPath.substring(0, queryIndex);
        }
        if(!cleanPath.startsWith("/")) {
            cleanPath = `/${cleanPath}`;
        }

        const newItem: PostPhotoItem = {
            title: driveFile?.fileName || "Drive Image",
            driveFileID: driveFile?._id,
            order: photos.length,
            path: cleanPath,
            previewUrl: url,
            source: "drive"
        };

        const updated = [
            ...photos,
            newItem
        ].map((p, idx) => ({
            ...p,
            order: idx
        }));
        onChange(updated);
    };

    const handleRemovePhoto = (index: number) => {
        if(disabled) {
            return;
        }
        const updated = photos.filter((_, idx) => idx !== index).map((p, idx) => ({
            ...p,
            order: idx
        }));
        onChange(updated);
    };

    const handleMakeCover = (index: number) => {
        if(disabled || index === 0) {
            return;
        }
        const target = photos[index];
        if(!target) {
            return;
        }
        const remaining = photos.filter((_, idx) => idx !== index);
        const updated = [
            target,
            ...remaining
        ].map((p, idx) => ({
            ...p,
            order: idx
        }));
        onChange(updated);
    };

    const handleMoveLeft = (index: number) => {
        if(disabled || index === 0) {
            return;
        }
        const newPhotos = [...photos];
        const temp = newPhotos[index - 1];
        const current = newPhotos[index];
        if(!temp || !current) {
            return;
        }
        newPhotos[index - 1] = current;
        newPhotos[index] = temp;
        onChange(newPhotos.map((p, idx) => ({
            ...p,
            order: idx
        })));
    };

    const handleMoveRight = (index: number) => {
        if(disabled || index >= photos.length - 1) {
            return;
        }
        const newPhotos = [...photos];
        const temp = newPhotos[index + 1];
        const current = newPhotos[index];
        if(!temp || !current) {
            return;
        }
        newPhotos[index + 1] = current;
        newPhotos[index] = temp;
        onChange(newPhotos.map((p, idx) => ({
            ...p,
            order: idx
        })));
    };

    const handleDragStart = (index: number, e: React.DragEvent<HTMLElement>) => {
        if(disabled) {
            return;
        }
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", index.toString());
    };

    const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
        if(disabled) {
            return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };

    const handleDrop = (targetIndex: number, e: React.DragEvent<HTMLElement>) => {
        if(disabled) {
            return;
        }
        e.preventDefault();
        if(draggedIndex === null || draggedIndex === targetIndex) {
            setDraggedIndex(null);
            return;
        }

        const newPhotos = [...photos];
        const [
            draggedItem
        ] = newPhotos.splice(draggedIndex, 1);
        if(!draggedItem) {
            setDraggedIndex(null);
            return;
        }
        newPhotos.splice(targetIndex, 0, draggedItem);
        setDraggedIndex(null);
        onChange(newPhotos.map((p, idx) => ({
            ...p,
            order: idx
        })));
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    marginBottom: spaces.spacingMd,
                    gap: spaces.spacingSm
                }
            ]}
        >
            <View
                style={[
                    stylesheet.titleContainer,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    style={{
                        color: colors.content.text.high
                    }}
                    variant="bodyMediumSize"
                >
                    {`${localize("post-photos-title")} (${photos.length}/${maxPhotos})`}
                </Text>
                <Text
                    style={{
                        color: colors.content.text.low
                    }}
                    variant="bodySmallSize"
                >
                    {`${localize("post-photos-description")} (${localize("events-cover-dimensions")})`}
                </Text>
            </View>
            <View
                style={[
                    stylesheet.headerActionsContainer,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Button
                    title={localize("post-photos-upload-computer")}
                    spreadBehaviour={isMobile ? "stretch" : "free"}
                    customTextColor={colors.project.brandWhite}
                    icon={({
                        size
                    }) => <UploadIcon
                        color={colors.project.brandWhite}
                        size={size}
                    />}
                    customBorderColor="secondaryButton"
                    onPress={handleFileInputClick}
                    customColor="secondaryButton"
                    isDisabled={disabled}
                    variant="outline"
                />
                <Button
                    spreadBehaviour={isMobile ? "stretch" : "free"}
                    onPress={() => setIsDriveModalVisible(true)}
                    title={localize("post-photos-select-drive")}
                    customTextColor={colors.project.brandWhite}
                    icon={({
                        size
                    }) => <FolderIcon
                        color={colors.project.brandWhite}
                        size={size}
                    />}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    isDisabled={disabled}
                    variant="outline"
                />
            </View>
        </View>;
    };

    const renderCard = (item: PostPhotoItem, index: number) => {
        const isCover = index === 0;
        const isDragged = draggedIndex === index;
        const cardWidth = isMobile ? "48%" : "23%";

        const dndProps = Platform.OS === "web" ? {
            onDragStart: (e: React.DragEvent<HTMLElement>) => handleDragStart(index, e),
            onDragOver: (e: React.DragEvent<HTMLElement>) => handleDragOver(e),
            onDrop: (e: React.DragEvent<HTMLElement>) => handleDrop(index, e),
            onDragEnd: handleDragEnd,
            draggable: !disabled
        } : {};

        return <View
            key={item._id || item.previewUrl || `photo-${index}`}
            style={[
                stylesheet.cardWrapper,
                {
                    width: cardWidth as unknown as number
                }
            ]}
            {...dndProps}
        >
            <View
                style={[
                    stylesheet.cardContainer,
                    isDragged ? stylesheet.dragActiveCard : null,
                    {
                        borderColor: isCover ? colors.project.brandPrimary : colors.project.brandBorder,
                        borderWidth: isCover ? borders.line * 2 : borders.seperator,
                        backgroundColor: colors.project.brandWhite,
                        borderRadius: radiuses.md
                    }
                ]}
            >
                <SmartCoverImage
                    containerStyle={stylesheet.cardImage}
                    url={item.previewUrl}
                />
                <View
                    style={[
                        stylesheet.cardHeaderOverlay,
                        {
                            padding: spaces.spacingXs
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.orderBadge,
                            {
                                backgroundColor: isCover ? colors.project.brandPrimary : colors.system.scrim,
                                paddingHorizontal: spaces.spacingSm,
                                paddingVertical: spaces.spacingXs,
                                borderRadius: radiuses.sm
                            }
                        ]}
                    >
                        <Text
                            style={[
                                stylesheet.orderBadgeText,
                                {
                                    color: colors.project.brandWhite
                                }
                            ]}
                            variant="bodySmallSize"
                        >
                            {isCover ? "★ 1" : `${index + 1}`}
                        </Text>
                    </View>
                    {
                        isCover ?
                            <View
                                style={[
                                    stylesheet.coverBadge,
                                    {
                                        backgroundColor: colors.project.brandPrimary,
                                        paddingHorizontal: spaces.spacingSm,
                                        paddingVertical: spaces.spacingXs,
                                        borderRadius: radiuses.sm,
                                        gap: spaces.spacingXs
                                    }
                                ]}
                            >
                                <StarIcon
                                    color={colors.project.brandWhite}
                                    size={12}
                                />
                                <Text
                                    style={[
                                        stylesheet.coverBadgeText,
                                        {
                                            color: colors.project.brandWhite
                                        }
                                    ]}
                                    variant="bodySmallSize"
                                >
                                    {localize("post-photos-cover-badge")}
                                </Text>
                            </View>
                        :
                            null
                    }
                </View>
                <View
                    style={[
                        stylesheet.cardActionsOverlay,
                        {
                            backgroundColor: colors.system.scrim,
                            padding: spaces.spacingXs,
                            gap: spaces.spacingXs
                        }
                    ]}
                >
                    {
                        !isCover && (
                            <TouchableOpacity
                                style={[
                                    stylesheet.makeCoverButton,
                                    {
                                        backgroundColor: colors.project.brandPrimary,
                                        paddingHorizontal: spaces.spacingSm,
                                        paddingVertical: spaces.spacingXs,
                                        borderRadius: radiuses.sm
                                    }
                                ]}
                                onPress={() => handleMakeCover(index)}
                                activeOpacity={0.7}
                            >
                                <Text
                                    style={[
                                        stylesheet.makeCoverText,
                                        {
                                            color: colors.project.brandWhite
                                        }
                                    ]}
                                    variant="bodySmallSize"
                                >
                                    {localize("post-photos-make-cover")}
                                </Text>
                            </TouchableOpacity>)
                    }
                    {
                        index > 0 && (
                            <TouchableOpacity
                                style={[
                                    stylesheet.iconActionButton,
                                    {
                                        backgroundColor: colors.system.scrim,
                                        borderRadius: radiuses.sm,
                                        padding: spaces.spacingXs
                                    }
                                ]}
                                onPress={() => handleMoveLeft(index)}
                                activeOpacity={0.7}
                            >
                                <ChevronLeftIcon
                                    color={colors.project.brandWhite}
                                    size={14}
                                />
                            </TouchableOpacity>)
                    }
                    {
                        index < photos.length - 1 && (
                            <TouchableOpacity
                                style={[
                                    stylesheet.iconActionButton,
                                    {
                                        backgroundColor: colors.system.scrim,
                                        borderRadius: radiuses.sm,
                                        padding: spaces.spacingXs
                                    }
                                ]}
                                onPress={() => handleMoveRight(index)}
                                activeOpacity={0.7}
                            >
                                <ChevronRightIcon
                                    color={colors.project.brandWhite}
                                    size={14}
                                />
                            </TouchableOpacity>)
                    }
                    <TouchableOpacity
                        onPress={() => setPreviewModalUrl(item.previewUrl)}
                        style={[
                            stylesheet.iconActionButton,
                            {
                                backgroundColor: colors.system.scrim,
                                borderRadius: radiuses.sm,
                                padding: spaces.spacingXs
                            }
                        ]}
                        activeOpacity={0.7}
                    >
                        <Maximize2Icon
                            color={colors.project.brandWhite}
                            size={14}
                        />
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={[
                            stylesheet.iconActionButton,
                            {
                                backgroundColor: colors.system.container.error,
                                borderRadius: radiuses.sm,
                                padding: spaces.spacingXs
                            }
                        ]}
                        onPress={() => handleRemovePhoto(index)}
                        activeOpacity={0.7}
                    >
                        <Trash2Icon
                            color={colors.project.brandWhite}
                            size={14}
                        />
                    </TouchableOpacity>
                </View>
            </View>
        </View>;
    };

    const renderEmpty = () => {
        return <TouchableOpacity
            style={[
                stylesheet.emptyContainer,
                {
                    borderColor: colors.project.brandBorder,
                    borderWidth: borders.seperator,
                    padding: spaces.spacingXl,
                    borderRadius: radiuses.md,
                    gap: spaces.spacingSm
                }
            ]}
            onPress={handleFileInputClick}
            disabled={disabled}
            activeOpacity={0.8}
        >
            <ImageIcon
                color={colors.content.icon.low}
                size={36}
            />
            <Text
                style={[
                    stylesheet.emptyTitleText,
                    {
                        color: colors.content.text.high
                    }
                ]}
                variant="bodyMediumSize"
            >
                {localize("post-photos-empty-title")}
            </Text>
            <Text
                style={[
                    stylesheet.emptyDescText,
                    {
                        color: colors.content.text.low
                    }
                ]}
                variant="bodySmallSize"
            >
                {localize("post-photos-empty-desc")}
            </Text>
            <Text
                style={{
                    color: colors.content.text.low
                }}
                variant="bodySmallSize"
            >
                {localize("events-cover-dimensions")}
            </Text>
        </TouchableOpacity>;
    };

    const renderLightboxModal = () => {
        if(!previewModalUrl) {
            return null;
        }

        return <Modal
            onRequestClose={() => setPreviewModalUrl(null)}
            animationType="fade"
            transparent={true}
            visible={true}
        >
            <TouchableOpacity
                style={[
                    stylesheet.lightboxModalBackdrop,
                    {
                        backgroundColor: colors.system.scrim
                    }
                ]}
                onPress={() => setPreviewModalUrl(null)}
                activeOpacity={1}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.lightboxCloseButton,
                        {
                            backgroundColor: colors.system.scrim,
                            borderRadius: radiuses.full,
                            padding: spaces.spacingSm,
                            right: spaces.spacingLg,
                            top: spaces.spacingLg
                        }
                    ]}
                    onPress={() => setPreviewModalUrl(null)}
                    activeOpacity={0.7}
                >
                    <XIcon
                        color={colors.project.brandWhite}
                        size={24}
                    />
                </TouchableOpacity>
                <View
                    style={stylesheet.lightboxImageContainer}
                >
                    <Image
                        style={stylesheet.lightboxImage}
                        source={{
                            uri: previewModalUrl
                        }}
                        resizeMode="contain"
                    />
                </View>
            </TouchableOpacity>
        </Modal>;
    };

    return <View
        style={stylesheet.container}
    >
        {
            Platform.OS === "web" && (
                <input
                    style={stylesheet.hiddenInput as React.CSSProperties}
                    onChange={handleFilesSelect}
                    ref={fileInputRef}
                    accept="image/*"
                    type="file"
                    multiple
                />)
        }
        {renderHeader()}
        {
            photos.length === 0 ?
                renderEmpty()
            :
                <View
                    style={[
                        stylesheet.gridContainer,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    {photos.map(renderCard)}
                </View>
        }
        <DriveSelectorModal
            onClose={() => setIsDriveModalVisible(false)}
            onSelect={handleDriveFileSelect}
            isVisible={isDriveModalVisible}
        />
        {renderLightboxModal()}
    </View>;
};
export default PostPhotosManager;
